// KÖZÖS kétsoros SZEMÉLY-cella: avatar + (locale-tudatos) név + opcionális jelölés a név mellett +
// másodlagos sor (e-mail / beosztás / bármi).
//
// MIÉRT `shared/`: HÁROM lista rendereli szó szerint ugyanezt a szerkezetet (kapcsolattartók: név + beosztás
// + „Elsődleges" badge · munkatársak: név + e-mail · eseménynapló: szereplő + e-mail), és mind a három saját
// `flex`/`gap`/`truncate` másolatot vitt. Ez pont az az eset, amit a repo DRY-szabálya a 2. előfordulásnál a
// közös rétegbe rendel — a harmadik előfordulásnál pedig már mérhető is a szórás: a `gap` és a másodlagos sor
// tipográfiája eltért.
//
// A KÜLÖNBSÉG a `PersonChip`-hez: az EGYSOROS chip (avatar + név) a lista-cellák és a felsorolások atomja; ez
// itt a KÉTSOROS változat, ahol a személy a sor TÁRGYA (az első oszlop), és a másodlagos azonosító
// (e-mail/beosztás) is kell a felismeréshez.
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@/components/ui/avatar';
import { Text } from '@/components/ui/text';
import { formatName } from '@/lib/format-name';
import { cn } from '@/lib/utils';

export interface PersonCellProps {
  /** Keresztnév (strukturált) — a locale-tudatos névsorrendhez. */
  givenName?: string | null;
  /** Vezetéknév (strukturált) — a locale-tudatos névsorrendhez. */
  familyName?: string | null;
  /** Fallback megjelenítendő név (displayName/e-mail), ha a strukturált részek hiányoznak. */
  name?: string | null;
  /** Presigned profilkép-URL; hiányzik/hibás → monogram-fallback. */
  avatarUrl?: string | null;
  /** Részleg szín-token (MORGENS-paletta) — az avatar háttere. */
  accentColor?: string | null;
  /**
   * Másodlagos sor (e-mail, beosztás). `null`/`undefined`/üres string → a sor NEM renderel (nincs üres hely).
   * `ReactNode`, mert a hívók a „nincs adat" jelölést (halvány em-dash) is ide adják — az a MEGJELENÍTÉS
   * része, nem külön állapot.
   */
  secondary?: ReactNode;
  /** A név MELLETT megjelenő jelölés (pl. „Elsődleges" badge). A hívó adja — a cella nem tud róla semmit. */
  badge?: ReactNode;
  /** Avatar-méret: `sm` (22px) / `md` (28px). Alap: `md` (a személy a sor tárgya). */
  size?: 'sm' | 'md';
  /**
   * A NÉV tipográfiája. `itemTitle` (alap) a lista-azonosító oszlop rendszer-szabálya (13/500). Az `inherit`
   * a KÖRNYEZŐ cella-tipográfiát tartja meg — ezt az eseménynapló használja, ahol a szereplő NEM a sor
   * azonosítója (a művelet az), és a lap design-ja rögzített.
   */
  nameVariant?: 'itemTitle' | 'inherit';
  className?: string;
}

export function PersonCell({
  givenName,
  familyName,
  name,
  avatarUrl,
  accentColor,
  secondary,
  badge,
  size = 'md',
  nameVariant = 'itemTitle',
  className,
}: PersonCellProps): JSX.Element {
  const { i18n } = useTranslation();
  const label = formatName({ givenName, familyName, displayName: name }, i18n.language);
  return (
    <div className={cn('flex min-w-0 items-center gap-2.5', className)}>
      <Avatar name={label} src={avatarUrl ?? null} size={size} accentColor={accentColor} />
      <div className="min-w-0 space-y-0.5">
        <div className="flex min-w-0 items-center gap-1.5">
          {nameVariant === 'inherit' ? (
            <div className="truncate">{label}</div>
          ) : (
            <Text variant="itemTitle" as="div" className="truncate">
              {label}
            </Text>
          )}
          {badge}
        </div>
        {secondary !== null && secondary !== undefined && secondary !== '' && (
          <Text tone="tertiary" as="div" className="truncate">
            {secondary}
          </Text>
        )}
      </div>
    </div>
  );
}
